(() => {
  if (window.FiivePersonalization) return;
  const KEY = 'fp:recommendations:v35';
  const DAY = 86400000;
  const kinds = new Set(['type', 'category', 'product']);
  let state = {events: [], seen: []};
  let purchases = [];
  let privacyReady = false;
  let purchaseController;
  let purchaseLoaded = false;
  let viewRecorded = false;
  let context = {};
  try { context = JSON.parse(document.getElementById('fp-interest-context')?.textContent || '{}'); } catch {}
  const api = () => window.Shopify?.customerPrivacy;
  const allowed = () => {
    try { return privacyReady && navigator.globalPrivacyControl !== true && api()?.preferencesProcessingAllowed() === true && api()?.analyticsProcessingAllowed() === true; }
    catch { return false; }
  };
  const age = at => Date.now() - Number(at);
  const validAge = (at, days) => Number.isFinite(Number(at)) && age(at) >= 0 && age(at) < days * DAY;
  const validValue = value => typeof value === 'string' && value.length > 0 && value.length <= 100;
  const clean = () => {
    state.events = state.events.filter(e => e && kinds.has(e.kind) && validValue(e.value) && validAge(e.at, 30) && Number.isFinite(e.weight) && e.weight > 0 && e.weight <= 5).slice(0, 100);
    state.seen = state.seen.filter(e => e && validValue(e.value) && validAge(e.at, 14)).slice(0, 160);
  };
  const persist = () => {
    if (!allowed()) return;
    clean();
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch {}
  };
  const purge = () => {
    state = {events: [], seen: []};
    purchases = [];
    purchaseLoaded = false;
    purchaseController?.abort();
    try { for (const key of [KEY, 'fp:discovery:v2', 'fp:discovery:interests:v1', 'fp:discovery:search:v1']) localStorage.removeItem(key); } catch {}
  };
  const record = (kind, value, weight = 1) => {
    if (!allowed() || !kinds.has(kind) || !validValue(value) || !Number.isFinite(weight)) return;
    state.events.unshift({kind, value, weight: Math.min(5, Math.max(.1, weight)), at: Date.now()});
    persist();
  };
  const loadPurchases = async () => {
    if (!allowed() || !context.signedIn || !document.querySelector('[data-fp-discovery]') || purchaseLoaded) return;
    purchaseLoaded = true;
    purchaseController = new AbortController();
    const url = new URL(context.root || '/', location.origin);
    url.searchParams.set('section_id', 'fiive-personalization-context');
    try {
      const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', signal: purchaseController.signal});
      if (!response.ok || !allowed()) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!allowed()) return;
      const records = JSON.parse(doc.querySelector('[data-fp-purchase-context]')?.textContent || '[]');
      if (Array.isArray(records)) purchases = records.filter(e => e && validValue(e.type) && validAge(Number(e.at) * 1000, 90)).slice(0, 50);
    } catch {}
  };
  const view = () => {
    if (viewRecorded || !allowed()) return;
    if (context.type) record('type', context.type, 1);
    if (/^\d+$/.test(String(context.id || ''))) record('product', String(context.id), 1);
    viewRecorded = true;
  };
  const sync = () => {
    if (!privacyReady || !api()) return;
    if (allowed()) {
      try {
        const saved = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (Array.isArray(saved?.events) && Array.isArray(saved?.seen)) state = saved;
      } catch {}
      clean();
      view();
      void loadPurchases();
    } else purge();
    document.dispatchEvent(new CustomEvent('fiive:personalization-change'));
  };
  const seen = handles => {
    if (!allowed()) return;
    const at = Date.now();
    state.seen = [...handles.filter(validValue).map(value => ({value, at})), ...state.seen].filter((e, i, all) => all.findIndex(item => item.value === e.value) === i);
    persist();
  };
  const affinity = (kind, value) => {
    if (!allowed() || !value) return 0;
    return Math.min(1, state.events.filter(e => e.kind === kind && e.value === value && validAge(e.at, 30)).reduce((sum, e) => sum + e.weight * Math.pow(.5, age(e.at) / (3 * DAY)), 0) / 5);
  };
  let resolveReady;
  const ready = new Promise(resolve => { resolveReady = resolve; });
  window.FiivePersonalization = {
    ready, allowed, record, seen, affinity,
    wasSeen: handle => allowed() && state.seen.some(e => e.value === handle && validAge(e.at, 14)),
    latestProductId: () => allowed() ? state.events.find(e => e.kind === 'product' && /^\d+$/.test(e.value) && validAge(e.at, 30))?.value || '' : '',
    purchaseAffinity: type => allowed() ? Math.min(1, purchases.filter(e => e.type === type).reduce((sum, e) => sum + Math.pow(.5, age(e.at * 1000) / (14 * DAY)), 0) / 3) : 0,
    reset: () => { purge(); viewRecorded = false; }
  };
  document.addEventListener('visitorConsentCollected', () => { if (api()) privacyReady = true; sync(); });
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const card = event.target.closest('[data-fp-dynamic-card]');
    if (card && event.target.closest('a,button')) {
      record('type', card.dataset.productType, 2);
      if (/^\d+$/.test(card.dataset.productId || '')) record('product', card.dataset.productId, 2);
      const panel = card.closest('[data-collection-handle]');
      const category = panel?.dataset.collectionHandle;
      if (category && !['recommended', 'fiive-new-arrivals', 'fiive-popular-products', 'all'].includes(category)) record('category', category, 2);
    }
    const tab = event.target.closest('[data-discovery-tab]');
    if (tab) record('category', document.getElementById(tab.dataset.panelId)?.dataset.collectionHandle, 2);
    if (event.target.closest('[data-fp-reset-recommendations]')) window.FiivePersonalization.reset();
  });
  const initialize = () => { privacyReady = Boolean(api()); sync(); resolveReady(); };
  if (api()) initialize();
  else if (window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], error => { if (error) resolveReady(); else initialize(); });
  else resolveReady();
})();
